/* File: src/contexts/gameHelpers.js */
// Frontend helpers - should primarily contain utilities needed by the UI or frontend game logic.

// Import only the constants needed by the REMAINING helper functions
import { DIFFICULTY_TIERS } from '../constants/gameConstants'; // Keep DIFFICULTY_TIERS for tier helpers

/**
 * Helper function to get tier object (name, color, etc.) from the precise difficulty score.
 * Uses the DIFFICULTY_TIERS_DISPLAY definition (which is the default export DIFFICULTY_TIERS).
 * SAFE TO KEEP ON FRONTEND: Used for UI display (FocusMeter).
 *
 * @param {number} difficulty - The precise difficulty score (0.00 - 0.95).
 * @returns {object} The tier object (e.g., { name: "Novice", min: 0.00, max: 0.15, color: "novice" }).
 */
export const getTierFromDifficulty = (difficulty) => {
  // Clamp difficulty to the expected display range (adjust max if necessary, e.g., 0.99)
  const clampedDifficulty = Math.max(0.00, Math.min(0.99, difficulty ?? 0.00));

  // Check ranges defined in DIFFICULTY_TIERS
  if (clampedDifficulty >= 0.80) return DIFFICULTY_TIERS.MASTER;
  if (clampedDifficulty >= 0.60) return DIFFICULTY_TIERS.EXPERT;
  if (clampedDifficulty >= 0.40) return DIFFICULTY_TIERS.ADEPT;
  if (clampedDifficulty >= 0.20) return DIFFICULTY_TIERS.APPRENTICE;
  return DIFFICULTY_TIERS.NOVICE; // Default
};

/**
 * Helper function to get only the tier name from the precise difficulty score.
 * Uses the getTierFromDifficulty helper.
 * SAFE TO KEEP ON FRONTEND: Used for UI display (FocusMeter, Results).
 *
 * @param {number} difficulty - The precise difficulty score (0.00 - 0.95).
 * @returns {string} The name of the difficulty tier (e.g., "Novice").
 */
export const getTierName = (difficulty) => {
  return getTierFromDifficulty(difficulty).name;
};


/**
 * Helper to determine if a trial constitutes a true match for a specific stimulus type.
 * This function directly compares stimuli values in the sequence array.
 * SAFE TO KEEP ON FRONTEND: Used by useGameResponses for instant feedback.
 *
 * @param {Array|null} sequence - The game sequence array.
 * @param {number} trialIndex - The index of the trial to check.
 * @param {number} nBack - The current n-back level.
 * @param {'visual' | 'audio'} type - The modality type to check ('visual' or 'audio').
 * @returns {boolean} True if the trial is a match of the specified type, false otherwise.
 */
export const isTrialMatch = (sequence, trialIndex, nBack, type) => {
    // Basic validation and boundary checks
    if (!sequence || !Array.isArray(sequence) || trialIndex < nBack || trialIndex >= sequence.length || nBack < 1) {
        // ---> ADDED/UNCOMMENTED LOG <---
        console.debug(`[isTrialMatch Util] Check failed (bounds/sequence): trial=${trialIndex}, nBack=${nBack}, seqLen=${sequence?.length}`);
        return false;
    }
    const currentItem = sequence[trialIndex];
    const nBackItem = sequence[trialIndex - nBack];

    // Check if items exist at those indices
    if (!currentItem || !nBackItem) {
        // ---> ADDED/UNCOMMENTED LOG <---
        console.debug(`[isTrialMatch Util] Check failed (item missing): trial=${trialIndex}, nBack=${nBack}`);
        return false;
    }

    // Determine which stimulus property to compare based on 'type'
    const currentStimulus = type === 'visual' ? currentItem.position : currentItem.letter;
    const nBackStimulus = type === 'visual' ? nBackItem.position : nBackItem.letter;

    // Check for null/undefined stimuli which cannot match
    if (currentStimulus === null || currentStimulus === undefined || nBackStimulus === null || nBackStimulus === undefined) {
        // ---> ADDED/UNCOMMENTED LOG <---
        console.debug(`[isTrialMatch Util] Check failed (stimulus null): trial=${trialIndex}, type=${type}, current=${currentStimulus}, nBackVal=${nBackStimulus}`);
        return false;
    }

    // Perform the actual comparison
    const isMatchResult = currentStimulus === nBackStimulus;
    // ---> ADDED/UNCOMMENTED LOG <---
    console.debug(`[isTrialMatch Util] Comparison: trial=${trialIndex}, type=${type}, current=${currentStimulus}, nBackVal=${nBackStimulus}. Result=${isMatchResult}`);
    return isMatchResult;
};


/**
 * Helper to determine if a trial is a lure for a specific type (n+1 or n-1 matches).
 * SAFE TO KEEP ON FRONTEND (for now): Potentially used for advanced feedback/analysis if needed.
 * Currently not essential if backend handles lure generation correctly.
 *
 * @param {Array|null} sequence - The game sequence array.
 * @param {number} trialIndex - The index of the trial to check.
 * @param {number} nBack - The current n-back level.
 * @param {'visual' | 'audio'} type - The modality type to check ('visual' or 'audio').
 * @returns {boolean} True if the trial is a lure of the specified type, false otherwise.
 */
export const isTrialLure = (sequence, trialIndex, nBack, type) => {
  // Need at least n-1 trials before the current one for n-1 check, and nBack must be >= 1
  if (!sequence || !Array.isArray(sequence) || trialIndex < (nBack > 1 ? nBack - 1 : 0) || trialIndex >= sequence.length || nBack < 1) {
    return false;
  }
  const currentItem = sequence[trialIndex];
  if (!currentItem) return false;

  const currentStimulus = type === 'visual' ? currentItem.position : currentItem.letter;

  // If current stimulus is null, it can't be a lure match
  if (currentStimulus === null || currentStimulus === undefined) {
      return false;
  }

  // Check n-1 position (only if nBack > 1)
  let isNMinus1Lure = false;
  if (nBack > 1) {
      const nMinus1Index = trialIndex - (nBack - 1);
      if (nMinus1Index >= 0) {
          const nMinus1Item = sequence[nMinus1Index];
          if (nMinus1Item) {
              const nMinus1Stimulus = type === 'visual' ? nMinus1Item.position : nMinus1Item.letter;
              isNMinus1Lure = nMinus1Stimulus !== null && nMinus1Stimulus !== undefined && currentStimulus === nMinus1Stimulus;
          }
      }
  }

  // Check n+1 position
  let isNPlus1Lure = false;
  const nPlus1Index = trialIndex - (nBack + 1);
  // Check if n+1 index is valid *and* if sequence has item at that index
  if (nPlus1Index >= 0) {
       const nPlus1Item = sequence[nPlus1Index];
       if (nPlus1Item) {
           const nPlus1Stimulus = type === 'visual' ? nPlus1Item.position : nPlus1Item.letter;
           isNPlus1Lure = nPlus1Stimulus !== null && nPlus1Stimulus !== undefined && currentStimulus === nPlus1Stimulus;
       }
   }

  // A lure is a match at n-1 OR n+1, but NOT an actual n-back match
  const isLureMatch = isNMinus1Lure || isNPlus1Lure;
  // Check if it's a true n-back match using the other helper
  const isTrueNBackMatch = isTrialMatch(sequence, trialIndex, nBack, type);

  return isLureMatch && !isTrueNBackMatch;
};


/**
 * Formats n-back and difficulty into a combined "N.DD" string.
 * Ensures difficulty (0.00-0.99 range allowed for display) is padded correctly.
 * SAFE TO KEEP ON FRONTEND: Pure UI formatting utility.
 *
 * @param {number} nBack - The n-back level (integer >= 1).
 * @param {number} difficulty - The difficulty (0.00 to 0.99).
 * @returns {string} Formatted string e.g., "2.05", "7.99". Returns "?.??" if inputs are invalid.
 */
export const formatCombinedDifficulty = (nBack, difficulty) => {
    // Validate inputs
    if (typeof nBack !== 'number' || !Number.isInteger(nBack) || nBack < 1 ||
    typeof difficulty !== 'number' || Number.isNaN(difficulty) || difficulty < -0.005 || difficulty > 0.995) { // Allow tolerance around 0.00-0.99
        // console.warn(`Invalid input to formatCombinedDifficulty: nBack=${nBack}, difficulty=${difficulty}`);
        return "?.??"; // Placeholder for invalid input
    }

    // Clamp difficulty strictly to 0.00-0.99 before formatting
    const clampedDifficulty = Math.max(0, Math.min(0.99, difficulty));
    // Multiply by 100 and round to get integer 0-99
    const difficultyHundredths = Math.round(clampedDifficulty * 100);
    // Pad with leading zero if needed (e.g., 5 becomes "05")
    const formattedDifficulty = String(difficultyHundredths).padStart(2, '0');

    return `${nBack}.${formattedDifficulty}`;
};


// --- REMOVED Backend-Specific Helpers ---
// calculateISI, getStimulusDuration, calculateTrials, calculateMatches,
// calculateLures, calculateOverlap, getStimuliSet, calculateSessionParameters
// These are now handled exclusively by the backend in functions/gameHelpers.js
// ------------------------------------------


// --- Default Export (Optional but common) ---
// Export the remaining frontend-relevant helpers
export default {
  getTierFromDifficulty,
  getTierName,
  isTrialMatch,
  isTrialLure,
  formatCombinedDifficulty
};